import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";

/**
 * BW19 PR-D — Inbox queries + actions.
 *
 * Reads awaiting_review captures and their triage rows, exposes mutations
 * for accept / edit-then-accept / reject / defer. Uses RLS-protected
 * direct Supabase access (no Worker hop needed).
 */

export type SuggestedActionItem = {
  title: string;
  category: string;
  effort_minutes: number;
  suggested_deadline: string | null;
  depends_on: string[];
};

export type TriageEntity = {
  kind: string;
  value: string;
  confidence: number;
};

export type InboxEntry = {
  capture: {
    id: string;
    content: string;
    source: string;
    captured_at: string;
    captured_in_tz: string | null;
  };
  triage: {
    id: string;
    type: string[];
    entities: TriageEntity[];
    health_implications: string | null;
    suggested_action_items: SuggestedActionItem[];
    suggested_project_id: string | null;
    suggested_thread_id: string | null;
  } | null;
};

export const ALLOWED_CATEGORIES = [
  "travel",
  "correspondence",
  "preparation",
  "errand",
  "decision",
  "research",
  "admin",
  "communication",
  "health",
  "financial",
] as const;
export type ActionCategory = (typeof ALLOWED_CATEGORIES)[number];

export function useInbox() {
  const { session } = useSession();
  const userId = session?.user?.id;

  return useQuery<InboxEntry[]>({
    queryKey: ["inbox", userId],
    enabled: Boolean(userId),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      if (!userId) return [];

      const { data: captures, error: capErr } = await supabase
        .from("capture")
        .select("id, content, source, captured_at, captured_in_tz, state, snooze_until")
        .eq("user_id", userId)
        .in("state", ["awaiting_review", "deferred"])
        .order("captured_at", { ascending: false });
      if (capErr) throw capErr;

      const now = new Date().toISOString();
      const visible = (captures ?? []).filter((c) => {
        if (c.state === "awaiting_review") return true;
        if (c.state === "deferred") {
          // Resurface deferred captures whose snooze has elapsed.
          return c.snooze_until != null && c.snooze_until <= now;
        }
        return false;
      });

      if (visible.length === 0) return [];

      const ids = visible.map((c) => c.id);
      const { data: triages, error: triErr } = await supabase
        .from("capture_triage")
        .select(
          "id, capture_id, type, entities, health_implications, suggested_action_items, suggested_project_id, suggested_thread_id, created_at",
        )
        .in("capture_id", ids)
        .order("created_at", { ascending: false });
      if (triErr) throw triErr;

      const byCapture = new Map<string, (typeof triages)[number]>();
      for (const t of triages ?? []) {
        if (!byCapture.has(t.capture_id)) byCapture.set(t.capture_id, t);
      }

      return visible.map((c) => {
        const t = byCapture.get(c.id);
        return {
          capture: {
            id: c.id,
            content: c.content,
            source: c.source,
            captured_at: c.captured_at,
            captured_in_tz: c.captured_in_tz,
          },
          triage: t
            ? {
                id: t.id,
                type: (t.type as string[]) ?? [],
                entities: (t.entities as TriageEntity[]) ?? [],
                health_implications: t.health_implications ?? null,
                suggested_action_items:
                  (t.suggested_action_items as SuggestedActionItem[]) ?? [],
                suggested_project_id: t.suggested_project_id ?? null,
                suggested_thread_id: t.suggested_thread_id ?? null,
              }
            : null,
        };
      });
    },
  });
}

export function useUnprocessedCount() {
  const { session } = useSession();
  const userId = session?.user?.id;
  return useQuery<number>({
    queryKey: ["inbox-unprocessed-count", userId],
    enabled: Boolean(userId),
    staleTime: 30_000,
    queryFn: async () => {
      if (!userId) return 0;
      const { count, error } = await supabase
        .from("capture")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .in("state", ["unprocessed", "triaging", "awaiting_review"]);
      if (error) throw error;
      return count ?? 0;
    },
  });
}

export function useAcceptCapture() {
  const qc = useQueryClient();
  const { session } = useSession();
  const userId = session?.user?.id;

  return useMutation({
    mutationFn: async (args: {
      capture_id: string;
      items: SuggestedActionItem[];
      suggested_project_id?: string | null;
      suggested_thread_id?: string | null;
    }) => {
      if (!userId) throw new Error("not signed in");
      if (args.items.length > 0) {
        const rows = args.items.map((it) => ({
          user_id: userId,
          title: it.title.slice(0, 200),
          category: ALLOWED_CATEGORIES.includes(it.category as ActionCategory)
            ? (it.category as ActionCategory)
            : "admin",
          state: "open",
          effort_estimate_min:
            Number.isFinite(it.effort_minutes) && it.effort_minutes > 0
              ? Math.round(it.effort_minutes)
              : null,
          due_at: it.suggested_deadline ?? null,
          capture_id: args.capture_id,
          project_id: args.suggested_project_id ?? null,
          thread_id: args.suggested_thread_id ?? null,
        }));
        const { error } = await supabase.from("action_item").insert(rows);
        if (error) throw error;
      }
      const { error: stateErr } = await supabase
        .from("capture")
        .update({ state: "accepted", reviewed_at: new Date().toISOString() })
        .eq("id", args.capture_id);
      if (stateErr) throw stateErr;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["inbox", userId] });
      void qc.invalidateQueries({ queryKey: ["inbox-unprocessed-count", userId] });
      void qc.invalidateQueries({ queryKey: ["action-items", userId] });
    },
  });
}

export function useRejectCapture() {
  const qc = useQueryClient();
  const { session } = useSession();
  const userId = session?.user?.id;
  return useMutation({
    mutationFn: async (capture_id: string) => {
      if (!userId) throw new Error("not signed in");
      const { error } = await supabase
        .from("capture")
        .update({ state: "rejected", reviewed_at: new Date().toISOString() })
        .eq("id", capture_id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["inbox", userId] });
      void qc.invalidateQueries({ queryKey: ["inbox-unprocessed-count", userId] });
    },
  });
}

export function useDeferCapture() {
  const qc = useQueryClient();
  const { session } = useSession();
  const userId = session?.user?.id;
  return useMutation({
    mutationFn: async (args: { capture_id: string; until: string }) => {
      if (!userId) throw new Error("not signed in");
      const { error } = await supabase
        .from("capture")
        .update({
          state: "deferred",
          snooze_until: args.until,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", args.capture_id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["inbox", userId] });
      void qc.invalidateQueries({ queryKey: ["inbox-unprocessed-count", userId] });
    },
  });
}
